import React, { useState } from 'react';
import { X } from 'lucide-react';
import { config } from '../../../config';
import './CorporatePaymentModal.css';

interface CorporatePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export function CorporatePaymentModal({ isOpen, onClose }: CorporatePaymentModalProps) {
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (!name) {
      setError('Name is required');
      return;
    }

    // Default email since the form no longer asks for it
    const defaultEmail = 'guest@luminar.com';

    setLoading(true);
    setError(null);

    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error('Could not load Razorpay. Please check your connection.');
      }

      const res = await fetch(`${config.API_BASE_URL}/tickets/initiate-razorpay-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ticketType: 'normal',
          eventType: 'marriage',
          quantity: 1,
          pricePerTicket: Number(amount),
          buyerName: name,
          buyerEmail: defaultEmail,
          buyerPhone: phone,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Payment initiation failed');
      
      // Mock mode fallback if credentials are not set on server
      if (data.mockMode) {
        const mockRes = await fetch(`${config.API_BASE_URL}/tickets/confirm-mock-payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ticketType: 'normal',
            eventType: 'marriage',
            quantity: 1,
            pricePerTicket: Number(amount),
            buyerName: name,
            buyerEmail: defaultEmail,
            buyerPhone: phone,
          }),
        });
        if (!mockRes.ok) throw new Error('Ticket creation failed');
        const created = await mockRes.json();
        const ticketIds = created.map((t: any) => t.id).join(',');
        window.location.href = `/payment-success?ticketIds=${ticketIds}&buyerName=${encodeURIComponent(name)}&buyerEmail=${encodeURIComponent(defaultEmail)}&buyerPhone=${encodeURIComponent(phone)}&totalAmount=${amount}`;
        return;
      }

      // Open Razorpay checkout popup
      const options = {
        key: data.razorpayKeyId,
        amount: Math.round(Number(amount) * 100), // in paise
        currency: 'INR',
        name: 'Luminar Entertainment',
        description: `Corporate Payment`,
        order_id: data.razorpayOrderId,
        prefill: {
          name: name,
          email: defaultEmail,
          contact: phone,
        },
        theme: {
          color: '#e10a1e',
        },
        handler: async (response: {
          razorpay_order_id: string;
          razorpay_payment_id: string;
          razorpay_signature: string;
        }) => {
          try {
            // Verify payment
            const verifyRes = await fetch(
              `${config.API_BASE_URL}/tickets/verify-razorpay-payment`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  ticketType: 'normal',
                  eventType: 'marriage',
                  quantity: 1,
                  pricePerTicket: Number(amount),
                  buyerName: name,
                  buyerEmail: defaultEmail,
                  buyerPhone: phone,
                }),
              },
            );

            if (!verifyRes.ok) {
              const err = await verifyRes.json();
              throw new Error(err.message || 'Verification failed');
            }

            const result = await verifyRes.json();
            const ticketIds = result.tickets.map((t: any) => t.id).join(',');
            window.location.href = `/payment-success?ticketIds=${ticketIds}&buyerName=${encodeURIComponent(name)}&buyerEmail=${encodeURIComponent(defaultEmail)}&buyerPhone=${encodeURIComponent(phone)}&totalAmount=${amount}`;
          } catch (err: any) {
            setError(err.message || 'Payment verification failed');
            setLoading(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (response: any) => {
        setError(
          response?.error?.description ||
          response?.error?.reason ||
          'Payment failed',
        );
        setLoading(false);
      });
      rzp.open();
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
      setLoading(false);
    }
  };

  return (
    <div className="corporate-modal-overlay" onClick={onClose}>
      <div className="corporate-modal" onClick={e => e.stopPropagation()}>
        <button className="corporate-modal__close" onClick={onClose}>
          <X size={24} />
        </button>
        
        <h2 className="corporate-modal__title">Corporate Payment</h2>
        <p className="corporate-modal__desc">
          Enter an amount to proceed with your payment securely via Razorpay.
        </p>

        <form className="corporate-modal__form" onSubmit={handleSubmit}>
          <div className="corporate-modal__group">
            <label htmlFor="amount">Amount ($)</label>
            <input
              id="amount"
              type="number"
              placeholder="e.g. 1000"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              required
              min="1"
            />
          </div>
          
          <div className="corporate-modal__group">
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              type="text"
              placeholder="John Doe"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </div>

          <div className="corporate-modal__group">
            <label htmlFor="phone">Phone Number (Optional)</label>
            <input
              id="phone"
              type="tel"
              placeholder="+91 9876543210"
              value={phone}
              onChange={e => setPhone(e.target.value)}
            />
          </div>

          {error && <div style={{ color: '#ff4444', fontSize: '0.9rem' }}>{error}</div>}

          <button type="submit" className="corporate-modal__submit" disabled={loading}>
            {loading ? 'Processing...' : 'Pay Now'}
          </button>
        </form>
      </div>
    </div>
  );
}
